/* ==========================================================================
   Inline quick-add on product cards (celebratevitamins) — feature/quick-add
   Markup: snippets/quick-add-card.liquid · Styling: assets/quick-add-card.css

   Loaded as type="module" (executes once even though each card includes the tag).

   Design:
   - The ADD is done by the theme's own ProductItem handler (theme.js:5555) via
     data-quick-shop-trigger="quick-add": AJAX add of 1, cart counter + drawer
     refresh, purchase confirmation popup. That popup can only be opened through
     the theme's internal event bus, so routing through the native button is required.
   - Our job: option selection, keeping data-product-id (+ the form's hidden id)
     on the selected variant, and updating price/image/availability.
   - Positional variant matching (theme.js:3848): variant.options[i] === selected[i].
   - Survives lazy-load / infinite-scroll / filtering via event delegation +
     a MutationObserver — no section IDs.
   - The native handler has no error handling or double-click guard. A failed add
     (422 / network) rejects its promise unhandled, so we catch that via
     `unhandledrejection`, plus a watchdog as a fallback. The UI is left intact.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__quickAddCardInit) return; // module runs once; guard anyway
  window.__quickAddCardInit = true;

  var SEL_ROOT = '[data-quick-add-root]';
  var WATCHDOG_MS = 8000;
  var ERROR_VISIBLE_MS = 5000;
  var inFlight = null; // { btn, st } of the add currently running

  /* ---- Money (clone of theme.js formatMoney) --------------------------- */

  var moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || moneyFormat;
    var match = formatString.match(re);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      default: value = formatWithDelimiters(cents, 2); break;
    }
    return formatString.replace(re, value);
  }

  function toArray(list) { return Array.prototype.slice.call(list || []); }

  /* ---- Per-card state -------------------------------------------------- */

  function getRoot(el) {
    return el && el.closest ? el.closest(SEL_ROOT) : null;
  }

  function getState(root) {
    if (!root) return null;
    if (root.__qa) return root.__qa;
    var meta = root.querySelector('[data-quick-add-card]');
    var json = root.querySelector('[data-quick-add-variants]');
    var variants = [];
    try { variants = JSON.parse(json ? json.textContent : '[]'); } catch (e) { variants = []; }

    var st = {
      card: meta || root,
      item: root,
      variants: variants,
      optionGroups: toArray(root.querySelectorAll('[data-quick-add-option]')),
      valueButtons: toArray(root.querySelectorAll('[data-quick-add-value]')),
      addButton: root.querySelector('[data-quick-add-button]'),
      addText: root.querySelector('[data-quick-add-text]'),
      variantInput: root.querySelector('[data-quick-add-variant-input]'),
      priceEl: root.querySelector('[data-quick-add-price]'),
      compareEl: root.querySelector('[data-quick-add-compare]'),
      discountEl: root.querySelector('[data-quick-add-discount]'),
      unitContainer: (meta || root).querySelector('[data-unit-price-container]'),
      unitPriceEl: (meta || root).querySelector('[data-unit-price]'),
      unitBaseEl: (meta || root).querySelector('[data-unit-base]'),
      errorEl: root.querySelector('[data-quick-add-error]'),
      imageEl: root.querySelector('.product-item__image--one img') || root.querySelector('.product-item__image img'),
      discountKind: meta ? meta.getAttribute('data-discount-kind') : null,
      discountTemplate: meta ? (meta.getAttribute('data-discount-template') || '') : '',
      selected: [],
      currentVariant: null
    };

    // Seed the FULL selection from the current variant so options hidden for having
    // a single value (not rendered as chips) still participate in positional matching.
    var startId = parseInt(meta && meta.getAttribute('data-current-variant-id'), 10);
    var start = null;
    for (var i = 0; i < variants.length; i++) { if (variants[i].id === startId) { start = variants[i]; break; } }
    if (!start) start = variants[0] || null;
    st.selected = start ? start.options.slice() : [];
    st.currentVariant = start;

    root.__qa = st;
    return st;
  }

  /* ---- Variant matching (positional) ----------------------------------- */

  function matchVariant(st) {
    for (var i = 0; i < st.variants.length; i++) {
      var v = st.variants[i], ok = true;
      for (var j = 0; j < st.selected.length; j++) {
        if (v.options[j] !== st.selected[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  // Value at 1-based option position `pos` is available if some AVAILABLE variant
  // carries it while matching the current selection for every OTHER option.
  function valueAvailable(st, pos, value) {
    for (var i = 0; i < st.variants.length; i++) {
      var v = st.variants[i];
      if (!v.available) continue;
      if (v.options[pos - 1] !== value) continue;
      var ok = true;
      for (var p = 0; p < st.selected.length; p++) {
        if (p + 1 === pos) continue;
        if (st.selected[p] != null && v.options[p] !== st.selected[p]) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  /* ---- Rendering ------------------------------------------------------- */

  function refresh(st) {
    st.currentVariant = matchVariant(st);
    updateAvailability(st);
    updatePrice(st);
    updateImage(st);
    updateAddButton(st);
  }

  function updateAvailability(st) {
    st.valueButtons.forEach(function (btn) {
      var pos = parseInt(btn.getAttribute('data-option-index'), 10);
      var value = btn.getAttribute('data-option-value');
      var selected = st.selected[pos - 1] === value;
      var available = selected || valueAvailable(st, pos, value);
      btn.classList.toggle('is-unavailable', !available);
      if (available) btn.removeAttribute('aria-disabled');
      else btn.setAttribute('aria-disabled', 'true');
    });
  }

  function updatePrice(st) {
    var v = st.currentVariant;
    if (!v) return;
    var onSale = v.compare_at_price != null && v.compare_at_price > v.price;

    if (st.priceEl) {
      st.priceEl.textContent = formatMoney(v.price);
      st.priceEl.classList.toggle('sale', onSale);
    }
    if (st.compareEl) {
      st.compareEl.textContent = onSale ? formatMoney(v.compare_at_price) : '';
    }
    if (st.discountEl) {
      st.discountEl.textContent = onSale ? discountText(st, v) : '';
    }
    if (st.unitContainer) {
      if (v.unit_price != null) {
        if (st.unitPriceEl) st.unitPriceEl.textContent = formatMoney(v.unit_price);
        if (st.unitBaseEl) st.unitBaseEl.textContent = v.unit_ref || '';
        st.unitContainer.hidden = false;
      } else {
        st.unitContainer.hidden = true;
      }
    }
  }

  function discountText(st, v) {
    var tpl = st.discountTemplate;
    if (st.discountKind === 'text') return tpl; // e.g. "Sale" — no placeholder
    if (st.discountKind === 'percent') {
      var pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
      return tpl.replace('__V__', pct);
    }
    // amount
    return tpl.replace('__V__', formatMoney(v.compare_at_price - v.price));
  }

  function updateImage(st) {
    var v = st.currentVariant;
    if (!st.imageEl) return;
    if (!v || !v.image) {
      if (st.item) st.item.classList.remove('product-item--variant-image');
      return;
    }
    if (st.imageEl.getAttribute('src') !== v.image) {
      st.imageEl.setAttribute('srcset', '');
      st.imageEl.setAttribute('src', v.image);
    }
    if (st.item) st.item.classList.add('product-item--variant-image');
  }

  function updateAddButton(st) {
    var btn = st.addButton;
    if (!btn) return;
    var v = st.currentVariant;
    var available = !!(v && v.available);

    if (v) {
      btn.setAttribute('data-product-id', v.id);
      if (st.variantInput) st.variantInput.value = v.id;
      st.card.setAttribute('data-current-variant-id', v.id);
    }

    if (btn.classList.contains('loading') || btn.__qaInFlight) return; // don't fight a live add

    var label = available ? btn.getAttribute('data-lang-add')
      : (v ? btn.getAttribute('data-lang-sold-out') : btn.getAttribute('data-lang-unavailable'));
    if (label) {
      if (st.addText) st.addText.textContent = label;
      btn.setAttribute('aria-label', label);
    }
    btn.disabled = !available;
    if (available) btn.removeAttribute('aria-disabled');
    else btn.setAttribute('aria-disabled', 'true');
  }

  /* ---- Interaction ----------------------------------------------------- */

  function onValueClick(card, chip) {
    if (chip.getAttribute('aria-disabled') === 'true') return;
    var st = getState(card);
    var pos = parseInt(chip.getAttribute('data-option-index'), 10);
    var value = chip.getAttribute('data-option-value');
    if (!pos) return;

    st.selected[pos - 1] = value;

    var group = chip.closest('[data-quick-add-option]');
    if (group) {
      toArray(group.querySelectorAll('[data-quick-add-value]')).forEach(function (c) {
        var on = c === chip;
        c.classList.toggle('is-selected', on);
        c.classList.toggle('selected', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    refresh(st);
  }

  /* ---- Add: reuse native handler, add guard + error handling ----------- */

  function showError(st, btn, message) {
    if (!st || !st.errorEl) return;
    st.errorEl.textContent = message || btn.getAttribute('data-lang-error') || '';
    st.errorEl.hidden = false;
    if (st.errorEl.__qaHide) clearTimeout(st.errorEl.__qaHide);
    st.errorEl.__qaHide = setTimeout(function () { st.errorEl.hidden = true; }, ERROR_VISIBLE_MS);
  }

  function endAdd(btn) {
    if (btn.__qaPoll) { clearInterval(btn.__qaPoll); btn.__qaPoll = null; }
    btn.__qaInFlight = false;
    if (inFlight && inFlight.btn === btn) inFlight = null;
    if (btn.__qaRoot) updateAddButton(getState(btn.__qaRoot));
  }

  function failAdd(btn, st, message) {
    btn.classList.remove('loading');
    endAdd(btn);
    showError(st, btn, message);
  }

  function armWatchdog(btn, st) {
    var start = Date.now();
    var sawLoading = false;
    btn.__qaPoll = setInterval(function () {
      var loading = btn.classList.contains('loading');
      if (loading) sawLoading = true;
      if (sawLoading && !loading) { endAdd(btn); return; } // native removed loading -> success
      if (Date.now() - start > WATCHDOG_MS) failAdd(btn, st);
    }, 150);
  }

  // Capture phase: runs BEFORE the theme's bubble-phase quick-add handler.
  function onAddCapture(e) {
    var btn = e.target.closest && e.target.closest('[data-quick-add-button]');
    if (!btn) return;
    var root = getRoot(btn);
    var st = root && getState(root);

    if (btn.classList.contains('loading') || btn.__qaInFlight || inFlight) { // double-click guard
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (btn.disabled || (st && st.currentVariant && !st.currentVariant.available)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showError(st, btn);
      return;
    }
    if (st && st.errorEl) st.errorEl.hidden = true;
    btn.__qaInFlight = true;
    btn.__qaRoot = root;
    inFlight = { btn: btn, st: st };
    armWatchdog(btn, st);
    // fall through: the native handler performs the add + confirmation popup
  }

  // The native add rejects unhandled on 422 ({ code, message }) or network failure.
  function onUnhandledRejection(e) {
    if (!inFlight) return;
    var reason = e.reason || {};
    var message = reason.code === 422 && reason.message ? reason.message : null;
    failAdd(inFlight.btn, inFlight.st, message);
  }

  /* ---- Init / dynamic content ------------------------------------------ */

  function initCard(card) {
    if (card.__qaInitDone) return;
    card.__qaInitDone = true;
    refresh(getState(card)); // upgrade the coarse server availability to per-combination
  }

  function initAll(scope) {
    toArray((scope || document).querySelectorAll(SEL_ROOT)).forEach(initCard);
  }

  function boot() {
    initAll(document);

    // Bubble delegation: option chip selection (works on cards added later).
    document.addEventListener('click', function (e) {
      var chip = e.target.closest && e.target.closest('[data-quick-add-value]');
      if (!chip) return;
      var root = getRoot(chip);
      if (root) { e.preventDefault(); e.stopPropagation(); onValueClick(root, chip); }
    });

    // Capture delegation: double-click guard + error watchdog on the add button.
    document.addEventListener('click', onAddCapture, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    // Re-init cards inserted by filtering / infinite scroll / lazy loading.
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches(SEL_ROOT)) initCard(node);
            if (node.querySelectorAll) initAll(node);
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
